import { ChangeEvent, useCallback, useLayoutEffect, useState } from "react";
import { Color } from "@tiptap/extension-color";
import Heading, { Level } from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { TableKit } from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import _ from "lodash";
import { marked } from "marked";

import {
  AddLinkIcon,
  BackupTableIcon,
  BorderColorIcon,
  FormatAlignCenterIcon,
  FormatAlignJustifyIcon,
  FormatAlignLeftIcon,
  FormatAlignRightIcon,
  FormatBoldIcon,
  FormatColorTextIcon,
  FormatItalicIcon,
  FormatListBulletedIcon,
  FormatListNumberedIcon,
  FormatSizeIcon,
  FormatStrikethroughIcon,
  FormatUnderlinedIcon,
} from "icons";
import { colors } from "theme/palette";

import Button from "components/Button";
import Collapse from "components/Collapse";
import IconButton from "components/IconButton";
import Menu, { MenuItem } from "components/Menu";
import Stack from "components/Stack";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";

export interface IRichTextEditor {
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
}

export interface IFormatOptions {
  editor: Editor | null;
  areTableToolsShown?: boolean;
  toggleTableTools?: () => void;
}

const CustomTableCell = TableCell.extend({
  // Allowing the cell note to hold an empty string
  content: "inline*",

  addAttributes() {
    return {
      // Ignoring some TS rules as the properties are actually there.
      // extend the existing attributes …
      // @ts-ignore
      ...this.parent?.(),
      // and add a new one …
      backgroundColor: {
        default: null,
        // @ts-ignore
        parseHTML: (element) => element.getAttribute("data-background-color"),
        // @ts-ignore
        renderHTML: (attributes) => {
          return {
            "data-background-color": attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`,
          };
        },
      },
    };
  },
});

const FormatOptions = ({
  editor,
  toggleTableTools,
  areTableToolsShown,
}: IFormatOptions) => {
  const [fontSizeElement, setFontSizeElement] = useState<HTMLElement | null>(
    null
  );

  const setLink = useCallback(() => {
    if (editor === null) {
      return;
    }

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    try {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    } catch (e) {
      toast.errorMessage(_.get(e, "message"));
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <Stack>
      <Stack
        gap={0.5}
        direction="row"
        sx={{
          padding: 0.5,
          borderRadius: "8px",
          backgroundColor: colors.white,
          flexWrap: "wrap",
        }}
      >
        <Tooltip title="Size" placement="top">
          <IconButton
            onClick={(event) => setFontSizeElement(event.currentTarget)}
          >
            <FormatSizeIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>

        <Menu
          disableScrollLock
          anchorEl={fontSizeElement}
          open={!_.isNil(fontSizeElement)}
          onClick={() => setFontSizeElement(null)}
          onClose={() => setFontSizeElement(null)}
        >
          {_.range(1, 7).map((level) => (
            <MenuItem
              key={level}
              selected={editor.isActive("heading", { level })}
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setHeading({ level: level as Level })
                  .run()
              }
            >
              Heading {level}
            </MenuItem>
          ))}
          <MenuItem
            selected={!editor.isActive("heading")}
            onClick={() => {
              const level = editor.getAttributes("heading").level;

              if (level > 0) {
                editor
                  .chain()
                  .focus()
                  .toggleHeading({
                    level: level,
                  })
                  .run();
              }
            }}
          >
            Normal
          </MenuItem>
        </Menu>

        <Tooltip title="Bold" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            sx={{
              backgroundColor: editor.isActive("bold") ? colors.blue40 : "",
            }}
          >
            <FormatBoldIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Italic" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            sx={{
              backgroundColor: editor.isActive("italic") ? colors.blue40 : "",
            }}
          >
            <FormatItalicIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Underline" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            sx={{
              backgroundColor: editor.isActive("underline")
                ? colors.blue40
                : "",
            }}
          >
            <FormatUnderlinedIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Strikethrough" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            sx={{
              backgroundColor: editor.isActive("strike") ? colors.blue40 : "",
            }}
          >
            <FormatStrikethroughIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Link" placement="top">
          <IconButton
            onClick={setLink}
            sx={{
              backgroundColor: editor.isActive("strike") ? colors.blue40 : "",
            }}
          >
            <AddLinkIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Bullet list" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            sx={{
              backgroundColor: editor.isActive("bulletList")
                ? colors.blue40
                : "",
            }}
          >
            <FormatListBulletedIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Numbered list" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            sx={{
              backgroundColor: editor.isActive("orderedList")
                ? colors.blue40
                : "",
            }}
          >
            <FormatListNumberedIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Align left" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            sx={{
              backgroundColor: editor.isActive({ textAlign: "left" })
                ? colors.blue40
                : "",
            }}
          >
            <FormatAlignLeftIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Align center" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            sx={{
              backgroundColor: editor.isActive({ textAlign: "center" })
                ? colors.blue40
                : "",
            }}
          >
            <FormatAlignCenterIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Align right" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            sx={{
              backgroundColor: editor.isActive({ textAlign: "right" })
                ? colors.blue40
                : "",
            }}
          >
            <FormatAlignRightIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Align justify" placement="top">
          <IconButton
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
            sx={{
              backgroundColor: editor.isActive({ textAlign: "justify" })
                ? colors.blue40
                : "",
            }}
          >
            <FormatAlignJustifyIcon sx={{ height: "15px" }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Text color" placement="top">
          <Stack position="relative">
            <IconButton>
              <FormatColorTextIcon sx={{ height: "15px" }} />
            </IconButton>
            <input
              type="color"
              onInput={(event: ChangeEvent<HTMLInputElement>) =>
                editor.chain().focus().setColor(event.target.value).run()
              }
              value={editor.getAttributes("textStyle").color}
              style={{
                height: "100%",
                width: "100%",
                position: "absolute",
                opacity: 0.3,
                cursor: "pointer",
              }}
            />
          </Stack>
        </Tooltip>

        <Tooltip title="Highlight" placement="top">
          <Stack position="relative">
            <IconButton>
              <BorderColorIcon sx={{ height: "15px" }} />
            </IconButton>
            <input
              type="color"
              onInput={(event: ChangeEvent<HTMLInputElement>) =>
                editor
                  .chain()
                  .focus()
                  .toggleHighlight({ color: event.target.value })
                  .run()
              }
              value={editor.getAttributes("highlight").color}
              style={{
                height: "100%",
                width: "100%",
                position: "absolute",
                opacity: 0.3,
                cursor: "pointer",
              }}
            />
          </Stack>
        </Tooltip>

        {!_.isNil(toggleTableTools) && (
          <Tooltip title="Table tools" placement="top">
            <IconButton
              onClick={toggleTableTools}
              sx={{
                backgroundColor: areTableToolsShown ? colors.blue40 : "",
              }}
            >
              <BackupTableIcon sx={{ height: "15px" }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Collapse in={areTableToolsShown} timeout="auto" unmountOnExit>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3 }).run()
            }
          >
            Insert table
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().addColumnBefore().run()}
            disabled={!editor.can().addColumnBefore()}
          >
            Add column before
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            disabled={!editor.can().addColumnAfter()}
          >
            Add column after
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            disabled={!editor.can().deleteColumn()}
          >
            Delete column
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().addRowBefore().run()}
            disabled={!editor.can().addRowBefore()}
          >
            Add row before
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            disabled={!editor.can().addRowAfter()}
          >
            Add row after
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().deleteRow().run()}
            disabled={!editor.can().deleteRow()}
          >
            Delete row
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().deleteTable().run()}
            disabled={!editor.can().deleteTable()}
          >
            Delete table
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              editor
                .chain()
                .focus()
                .setCellAttribute("backgroundColor", "#FAF594")
                .run()
            }
            disabled={
              !editor.can().setCellAttribute("backgroundColor", "#FAF594")
            }
          >
            Highlight cell
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().goToNextCell().run()}
            disabled={!editor.can().goToNextCell()}
          >
            Go to next cell
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().goToPreviousCell().run()}
            disabled={!editor.can().goToPreviousCell()}
          >
            Go to previous cell
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => editor.chain().focus().fixTables().run()}
            disabled={!editor.can().fixTables()}
          >
            Fix tables
          </Button>
        </Stack>
      </Collapse>
    </Stack>
  );
};

const RichTextEditor = ({ value, onChange, placeholder }: IRichTextEditor) => {
  const [areTableToolsShown, setAreTableToolsShown] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      TableKit,
      TableRow,
      TableHeader,
      CustomTableCell,
      Underline,
      TextStyleKit,
      Color,
      Placeholder.configure({
        // Use a placeholder:
        placeholder,
      }),
      Highlight.configure({ multicolor: true }),
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6],
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        protocols: ["http", "https"],
        isAllowedUri: (url, ctx) => {
          try {
            // construct URL
            const parsedUrl = url.includes(":")
              ? new URL(url)
              : new URL(`${ctx.defaultProtocol}://${url}`);

            // use default validation
            if (!ctx.defaultValidate(parsedUrl.href)) {
              return false;
            }

            // disallowed protocols
            const disallowedProtocols = ["ftp", "file", "mailto"];
            const protocol = parsedUrl.protocol.replace(":", "");

            if (disallowedProtocols.includes(protocol)) {
              return false;
            }

            // only allow protocols specified in ctx.protocols
            const allowedProtocols = ctx.protocols.map((p) =>
              typeof p === "string" ? p : p.scheme
            );

            if (!allowedProtocols.includes(protocol)) {
              return false;
            }

            // all checks have passed
            return true;
          } catch {
            return false;
          }
        },
      }),
    ],
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
  });

  useLayoutEffect(() => {
    if (_.isNil(editor)) {
      return;
    }

    const { from, to } = editor.state.selection;

    const content = marked(value || "", {
      breaks: true,
      gfm: true,
      async: false,
    });

    editor.commands.setContent(content, {
      emitUpdate: false,
      parseOptions: {
        preserveWhitespace: true,
      },
    });
    editor.commands.setTextSelection({ from, to });
  }, [editor, value]);

  const handleWrapperClick = useCallback(() => {
    if (editor && !editor.isFocused) {
      editor.commands.focus("end");
    }
  }, [editor]);

  return (
    <>
      <FormatOptions
        editor={editor}
        areTableToolsShown={areTableToolsShown}
        toggleTableTools={() =>
          setAreTableToolsShown((prevValue) => !prevValue)
        }
      />
      <Stack
        onClick={handleWrapperClick}
        sx={{
          flexGrow: 1,
          maxHeight: "40vh",
          overflowY: "auto",
          borderRadius: "8px",
          border: `1px solid ${colors.blue40}`,
          ".tiptap": {
            paddingX: 2,
            paddingBottom: 1,
            outline: "none",
          },
          ".tippy-box": {
            borderRadius: "4px",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
          },
          p: {
            margin: 0,
          },
          table: {
            borderCollapse: "collapse",
            tableLayout: "fixed",
            width: "100%",
            margin: 0,
            overflow: "hidden",
            td: {
              minWidth: "10em",
              border: "2px solid #ced4da",
              padding: "3px 5px",
              verticalAlign: "top",
              boxSizing: "border-box",
              position: "relative",
              "& > *": {
                marginBottom: 0,
              },
            },
            th: {
              minWidth: "10em",
              border: "2px solid #ced4da",
              padding: "3px 5px",
              verticalAlign: "top",
              boxSizing: "border-box",
              position: "relative",
              fontWeight: "bold",
              textAlign: "left",
              backgroundColor: "#f1f3f5",
              "& > *": {
                marginBottom: 0,
              },
            },
            ".selectedCell:after": {
              zIndex: 2,
              position: "absolute",
              content: '""',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              background: "rgba(200, 200, 255, 0.4)",
              pointerEvents: "none",
            },
            ".column-resize-handle": {
              position: "absolute",
              right: "-2px",
              top: 0,
              bottom: "-2px",
              width: "4px",
              backgroundColor: "#adf",
              pointerEvents: "none",
            },
          },
        }}
      >
        <EditorContent editor={editor} data-testid="rtf-edit-area" />
      </Stack>
    </>
  );
};

export default RichTextEditor;
